import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  MenuItem,
  Chip,
  Link,
} from '@mui/material';
import { CheckCircle, Cancel, Science, Straighten, Print } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  updateQualityCheckpoint,
  submitQualityInspection,
  setSelectedGauge,
  QUALITY_REPORT_URL,
} from '../../../store/slices/operatorSlice';

const QualityCheck: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    qualityCheckpoints,
    qualityPlan,
    activeOperation,
    gauges,
    selectedGaugeCode,
    lastInspection,
    isSubmittingQuality,
    error,
  } = useSelector((state: RootState) => state.operator);

  const hasMeasurements = qualityCheckpoints.some((cp) => cp.type === 'measurement');

  const allAnswered = qualityCheckpoints.every((cp) =>
    cp.type === 'measurement'
      ? cp.measuredValue !== undefined && cp.measuredValue !== '' && cp.result !== undefined
      : cp.result !== undefined
  );

  const hasFailed = qualityCheckpoints.some((cp) => cp.result === 'fail');
  const gaugeRequired = hasMeasurements && !selectedGaugeCode;

  const handleResultChange = (id: string, result: 'pass' | 'fail') => {
    dispatch(updateQualityCheckpoint({ id, result }));
  };

  const handleMeasurementChange = (id: string, value: string) => {
    dispatch(updateQualityCheckpoint({ id, measuredValue: value }));
  };

  const handleSubmit = () => {
    dispatch(submitQualityInspection());
  };

  if (!activeOperation) {
    return (
      <Alert severity="info" icon={<Science />}>
        Select a work order to view quality checkpoints.
      </Alert>
    );
  }

  if (!qualityPlan || qualityCheckpoints.length === 0) {
    return (
      <Alert severity="warning">
        No active control plan is attached to this operation. Contact QC to create and activate one
        before recording inspections.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6">{qualityPlan.planName}</Typography>
            <Typography variant="caption" color="textSecondary">
              {qualityPlan.inspectionType.replace(/_/g, ' ').toUpperCase()} · sample size {qualityPlan.sampleSize}
              {qualityPlan.revision ? ` · ${qualityPlan.revision}` : ''}
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {qualityCheckpoints.filter((c) => c.result !== undefined).length} / {qualityCheckpoints.length} completed
          </Typography>
        </Box>

        {hasMeasurements && (
          <TextField
            label="Measuring tool / IMTP no."
            value={selectedGaugeCode || ''}
            onChange={(e) => dispatch(setSelectedGauge(e.target.value || null))}
            select
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <Straighten sx={{ mr: 1, color: 'action.active' }} /> }}
            helperText={
              gauges.length === 0
                ? 'No calibrated gauges registered — measurements will be recorded without a tool reference'
                : 'Gauge used for the dimensional checks below (calibration is verified on submit)'
            }
          >
            {gauges.map((g) => (
              <MenuItem key={g.id} value={g.gaugeCode}>
                {g.gaugeCode} — {g.gaugeName}
              </MenuItem>
            ))}
          </TextField>
        )}

        {hasFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            One or more checkpoints have failed. Do not pass the part. Notify your supervisor — a
            failed inspection will hold the operation at the quality gate.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {qualityCheckpoints.map((checkpoint, index) => (
            <Box key={checkpoint.id}>
              {index > 0 && <Divider sx={{ mb: 2 }} />}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                <Box flex={1} minWidth={200}>
                  <Typography variant="body1" fontWeight="medium">
                    {index + 1}. {checkpoint.name}
                  </Typography>
                  {checkpoint.specification && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Spec: {checkpoint.specification}
                    </Typography>
                  )}
                  {checkpoint.method && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Method: {checkpoint.method}
                    </Typography>
                  )}
                </Box>

                {checkpoint.type === 'pass_fail' ? (
                  <ToggleButtonGroup
                    value={checkpoint.result || null}
                    exclusive
                    onChange={(_, value) => {
                      if (value) handleResultChange(checkpoint.id, value);
                    }}
                    size="small"
                  >
                    <ToggleButton
                      value="pass"
                      sx={{
                        '&.Mui-selected': { bgcolor: 'success.light', color: 'success.dark', borderColor: 'success.main' },
                        minWidth: 80,
                      }}
                    >
                      <CheckCircle sx={{ mr: 0.5, fontSize: 18 }} /> PASS
                    </ToggleButton>
                    <ToggleButton
                      value="fail"
                      sx={{
                        '&.Mui-selected': { bgcolor: 'error.light', color: 'error.dark', borderColor: 'error.main' },
                        minWidth: 80,
                      }}
                    >
                      <Cancel sx={{ mr: 0.5, fontSize: 18 }} /> FAIL
                    </ToggleButton>
                  </ToggleButtonGroup>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      label={`Value (${checkpoint.uom || ''})`}
                      value={checkpoint.measuredValue || ''}
                      onChange={(e) => handleMeasurementChange(checkpoint.id, e.target.value)}
                      size="small"
                      type="number"
                      sx={{ width: 140 }}
                      placeholder="Measurement"
                    />
                    {checkpoint.result && (
                      <Chip
                        label={checkpoint.result.toUpperCase()}
                        color={checkpoint.result === 'pass' ? 'success' : 'error'}
                        size="small"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        <Box mt={3}>
          <Button
            variant="contained"
            color={hasFailed ? 'error' : 'success'}
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={!allAnswered || gaugeRequired || isSubmittingQuality}
            startIcon={isSubmittingQuality ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            sx={{ py: 1.5 }}
          >
            {hasFailed
              ? 'Submit Inspection — FAILED'
              : 'Submit Inspection & Sign (Operator)'}
          </Button>
          {!allAnswered && (
            <Typography variant="caption" color="textSecondary" display="block" textAlign="center" mt={0.5}>
              Complete all checkpoints before submitting
            </Typography>
          )}
          {gaugeRequired && (
            <Typography variant="caption" color="error" display="block" textAlign="center" mt={0.5}>
              Select the measuring tool (IMTP no.) used for the dimensional checks
            </Typography>
          )}
        </Box>

        {lastInspection && (
          <Alert
            severity={lastInspection.overallResult === 'pass' ? 'success' : 'warning'}
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<Print />}
                component={Link}
                href={QUALITY_REPORT_URL(lastInspection.id)}
                target="_blank"
                rel="noopener"
              >
                Report
              </Button>
            }
          >
            Inspection recorded and operator-signed ({lastInspection.approvalStatus.replace(/_/g, ' ')}).
            {lastInspection.overallResult === 'pass'
              ? ' Awaiting QC sign-off on the Quality page to release this operation.'
              : ' Failed — the operation is held at the quality gate. Notify QC.'}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default QualityCheck;
